import { Box, Typography, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { AnswerMetadataType, BusinessComponentDetailsMetadataDto, ScheduleRespondentAnswerDto } from '../../../../../dtos';
import { FormInput } from '../../../../CoreLib/library';
import { BusinessComponentLabel } from '../BusinessComponentLabel';
import { textAlignCenterOnMobile } from '../../util';
import { patterns } from '../../../FormFields';
import { SurveyFormErrorMessage } from '../../../types';
import _ from 'lodash';

export interface ITAQByBCSurveyContentRendererProps {
    isPreview?: boolean;
    respondentAnswers: ScheduleRespondentAnswerDto[];
    setQuestionAnswer: (fieldName: string, value: string | number) => void;
    isReadOnly: boolean;
    isSavingAnswers: boolean;
    errorMessages: SurveyFormErrorMessage[];
    setErrorMessages: (updatedErrors: SurveyFormErrorMessage[]) => void;
    isPdfView?: boolean;
}

export const TAQByBCSurveyContentRenderer: FC<ITAQByBCSurveyContentRendererProps> = ({ isPreview, respondentAnswers, setQuestionAnswer, isReadOnly, isSavingAnswers, errorMessages, setErrorMessages, isPdfView }) => {
    const theme = useTheme();
    const TAQByBCEntryBusinessComponents = useMemo(() => {
        return respondentAnswers.filter((answerEntry) => answerEntry.metadata?.type === AnswerMetadataType.BusinessComponentDetails).map((answerEntry) => ({
            ...answerEntry,
            businessComponentInfo: answerEntry.metadata && (JSON.parse(answerEntry.metadata.metadata) as BusinessComponentDetailsMetadataDto),
        })).sort((a, b) => (a.businessComponentInfo?.name.toLowerCase() ?? '').localeCompare(b.businessComponentInfo?.name.toLowerCase() ?? ''));
    }, [respondentAnswers]);

    const TAQByBCEntryActivities = useMemo(() => {
        return respondentAnswers.filter((answerEntry) => answerEntry.metadata?.type === AnswerMetadataType.ActivityDetails).map((answerEntry) => ({
            ...answerEntry,
            businessComponentInfo: answerEntry.metadata && (JSON.parse(answerEntry.metadata.metadata) as BusinessComponentDetailsMetadataDto),
        })).sort((a, b) => (a.businessComponentInfo?.name.toLowerCase() ?? '').localeCompare(b.businessComponentInfo?.name.toLowerCase() ?? ''));
    }, [respondentAnswers]);

    const validatePercentageInput = useCallback((value: string | number) => {
        if (value === '') {
            return '';
        }
        let numericValue = Number(value);
        if (isNaN(numericValue)) {
            return 'Value must be a valid number.';
        } else if (numericValue < 0) {
            return 'Value cannot be less than 0.';
        } else if (numericValue > 100) {
            return 'Value cannot be greater than 100.';
        } else {
            return '';
        }
    }, []);

    const totalPercentageBusinessComponents = useMemo(
        () =>
            TAQByBCEntryBusinessComponents
                .map((answerEntry) => Number(answerEntry.answerValue))
                .filter((answerValue) => !isNaN(answerValue))
                .reduce((total, value) => total += value, 0),
        [TAQByBCEntryBusinessComponents]
    );

    const totalPercentageActivities = useMemo(

        () =>
            TAQByBCEntryActivities
                .map((answerEntry) => Number(answerEntry.answerValue))
                .filter((answerValue) => !isNaN(answerValue))
                .reduce((total, value) => total += value, 0),
        [TAQByBCEntryActivities]
    );

    const totalPercentage = totalPercentageBusinessComponents + totalPercentageActivities;

    useEffect(() => {
        const currentErrorMessages = respondentAnswers
                                        .map<SurveyFormErrorMessage>(answerEntry => ({identifier: answerEntry.fieldKey, message: validatePercentageInput(answerEntry.answerValue)}))
                                        .filter(formError => !!formError.message);
        if (totalPercentage > 100) {
            currentErrorMessages.push({
                identifier: 'total',
                message: 'total may not exceed 100%.'
            });
        }

        if (totalPercentage < 100) {
            currentErrorMessages.push({
                identifier: 'total',
                message: 'total may not be less than 100%.'
            });
        }

        const newErrorMessages = _.differenceBy(currentErrorMessages, errorMessages, (x) => x.identifier);
        const outdatedErrorMessages = _.differenceBy(errorMessages, currentErrorMessages, (x) => x.identifier);
        if (newErrorMessages.length > 0 || outdatedErrorMessages.length > 0) {
            setErrorMessages(currentErrorMessages);
        }
    }, [respondentAnswers, errorMessages, setErrorMessages, validatePercentageInput, totalPercentage]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 3 }}>
            <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'auto' }, [theme.breakpoints.down('sm')]: { marginY: 2 } }}>
                    <Typography
                        variant='h2'
                        sx={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#ffffff',
                            backgroundColor: '#606162',
                            padding: '4px 8px',
                            ...textAlignCenterOnMobile,
                        }}>
                        I. Business Components
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 1, mb: 2, fontWeight: 'bold' }}>
                <Box sx={{ width: 200, textAlign: 'end' }}>
                    Percentage of Time
                </Box>
            </Box>
            {TAQByBCEntryBusinessComponents.length === 0 && (
                <Typography textAlign='center' pt={2}>
                    {' '}
                    There are no Business Components at this time.{' '}
                </Typography>
            )}
            {/* If performance becomes an issue here we may be able to replace this with a FixedSizeList to control how many elements need to be re-rendered */}
            {TAQByBCEntryBusinessComponents.map((taqByBcEntry) => (
                <Box key={taqByBcEntry.fieldKey} sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', flexWrap: 'nowrap', py: 2 }} className={!isPdfView ? "standard-survey-form-row" : undefined}>
                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <BusinessComponentLabel bcInfo={taqByBcEntry.businessComponentInfo} />
                    </Box>
                    <Box sx={{ display: 'flex', flex: '0 0 auto', minWidth: 100, gap: 1 }}>
                        <Box sx={{ flex: '1 1 0' }}>
                        <FormInput
                            label={''}
                            value={taqByBcEntry.answerValue.toString()}
                            onChange={(event) => setQuestionAnswer(taqByBcEntry.fieldKey, event.target.value)}
                            inputProps={{ inputMode: 'numeric', pattern: patterns.Numeric.validate.source, className: 'standard-outlined-input' }}
                            fullWidth
                            disabled={isPreview || isReadOnly || isSavingAnswers}
                            error={!!validatePercentageInput(taqByBcEntry.answerValue)}
                            errorText={validatePercentageInput(taqByBcEntry.answerValue)}
                        />
                        </Box>
                        <Box sx={{ alignSelf: 'center', flex: '0 0 auto' }}>
                            <>%</>
                        </Box>
                    </Box>
                </Box>
            ))}
            <Box sx={{ textAlign: 'right' }}>
                <Typography fontWeight='bold'>
                    Subtotal Business Components <span style={{ marginLeft: '40px' }}>{totalPercentageBusinessComponents}%</span>
                </Typography>
            </Box>
           {TAQByBCEntryActivities.length > 0 && <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ width: { xs: '100%', sm: 'auto' }, [theme.breakpoints.down('sm')]: { marginY: 2 } }}>
                    <Typography
                        variant='h2'
                        sx={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#ffffff',
                            backgroundColor: '#606162',
                            padding: '4px 8px',
                            ...textAlignCenterOnMobile,
                        }}>
                        II. Activities
                    </Typography>
                </Box>
            </Box>}
            {TAQByBCEntryActivities.map((taqByBcEntry) => (
                <Box key={taqByBcEntry.fieldKey} sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', flexWrap: 'nowrap', py: 2 }} className={!isPdfView ? "standard-survey-form-row" : undefined}>
                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <BusinessComponentLabel bcInfo={taqByBcEntry.businessComponentInfo} />
                    </Box>
                    <Box sx={{ display: 'flex', flex: '0 0 auto', minWidth: 100, gap: 1 }}>
                        <Box sx={{ flex: '1 1 0' }}>
                        <FormInput
                            label={''}
                            value={taqByBcEntry.answerValue.toString()}
                            onChange={(event) => setQuestionAnswer(taqByBcEntry.fieldKey, event.target.value)}
                            inputProps={{ inputMode: 'numeric', pattern: patterns.Numeric.validate.source, className: 'standard-outlined-input' }}
                            fullWidth
                            disabled={isPreview || isReadOnly || isSavingAnswers}
                            error={!!validatePercentageInput(taqByBcEntry.answerValue)}
                            errorText={validatePercentageInput(taqByBcEntry.answerValue)}
                        />
                        </Box>
                        <Box sx={{ alignSelf: 'center', flex: '0 0 auto' }}>
                            <>%</>
                        </Box>
                    </Box>
                </Box>
            ))}
            {TAQByBCEntryActivities.length > 0 && <Box sx={{ textAlign: 'right' }}>
                <Typography fontWeight='bold'>
                    Subtotal Activities <span style={{ marginLeft: '40px' }}>{totalPercentageActivities}%</span>
                    </Typography>
                </Box>}
            <Box sx={{ textAlign: 'right' }}>
                <Typography fontWeight='bold'>
                    Total <span style={{ marginLeft: '40px' }}>{totalPercentage}%</span>
                </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
                {!isPreview && <Typography fontWeight='bold' color='error'>
                    {totalPercentage > 100 && 'The total percentage of time may not exceed 100%.'}
                    {totalPercentage < 100 && 'The total percentage of time may not be less than 100%.'}
                </Typography>}
            </Box>
        </Box>
    );
};
